import React, { useCallback, useEffect, useState } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';
import { formatPhone } from '../phone';
import { pushSupport, subscribeToPush, unsubscribeFromPush } from '../push';

type Pending = {
  username: string;
  email: string;
  name: string;
  phone: string;
  kids: string;
  needsTent: boolean;
  needsRide: boolean;
  offersTent: boolean;
  offersRide: boolean;
  dietary: string;
  bringing: string;
  requested: string;
};

const APPROVAL_TEMPLATE = (firstName: string, signedBy: string) =>
  `Hey ${firstName}, Thanks for registering for the camping trip. Excited to see you're coming. ` +
  `I approved the login. Please feel free to browse the site to see the location and who all is coming. ` +
  `Thanks again!${signedBy ? ` ${signedBy}` : ''}`;

function firstNameOf(fullName: string, fallback = ''): string {
  return (fullName || '').trim().split(/\s+/)[0] || fallback;
}

/**
 * Opens the phone's messaging app with the recipient and body filled in.
 * The "?&" is deliberate: iOS and Android disagree on the separator, and this
 * form is the one both accept.
 */
function approvalSmsHref(phone: string, fullName: string, signedBy: string): string {
  const body = encodeURIComponent(APPROVAL_TEMPLATE(firstNameOf(fullName, 'there'), signedBy));
  return `sms:${phone}?&body=${body}`;
}

/**
 * Approve pending registrations from the site.
 *
 * Only rendered for admins, but that's cosmetic — the Lambda re-checks the
 * caller's custom:is.admin on every request.
 */
export default function AdminPanel(): JSX.Element {
  const endpoint = process.env.REACT_APP_ADMIN_URL;
  const [pending, setPending] = useState<Pending[] | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');
  const [justApproved, setJustApproved] = useState<Pending | null>(null);
  const [push, setPush] = useState(false);
  const [notifyBusy, setNotifyBusy] = useState('');
  const [adminName, setAdminName] = useState('');
  const [confirmingDecline, setConfirmingDecline] = useState('');
  const support = pushSupport();

  const call = useCallback(
    async (init?: RequestInit) => {
      const session = await fetchAuthSession();
      const token = session.tokens?.accessToken?.toString();
      if (!token) throw new Error('no token');

      const res = await fetch(endpoint as string, {
        ...init,
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
          ...(init?.headers ?? {}),
        },
      });

      if (!res.ok) throw new Error(String(res.status));
      return res.json();
    },
    [endpoint]
  );

  useEffect(() => {
    if (!endpoint) {
      setError('The admin endpoint is not set up yet.');
      return;
    }

    let cancelled = false;
    call()
      .then(body => {
        if (cancelled) return;
        setPending(body.pending ?? []);
        setPush(Boolean(body.push));
        setAdminName(body.adminName ?? '');
      })
      .catch(() => {
        if (!cancelled) setError("Couldn't load pending registrations.");
      });

    return () => {
      cancelled = true;
    };
  }, [endpoint, call]);

  async function handleApprove(person: Pending) {
    setError('');
    setBusy(person.username);
    try {
      const body = await call({
        method: 'POST',
        body: JSON.stringify({ username: person.username }),
      });
      setPending(body.pending ?? []);
      setJustApproved(person);
    } catch {
      setError(`Couldn't approve ${person.name || person.email}. Try again.`);
    } finally {
      setBusy('');
    }
  }

  async function handleDecline(person: Pending) {
    setError('');
    setBusy(person.username);
    try {
      const body = await call({
        method: 'POST',
        body: JSON.stringify({ action: 'decline', username: person.username }),
      });
      setPending(body.pending ?? []);
      setConfirmingDecline('');
    } catch {
      setError(`Couldn't decline ${person.name || person.email}. Try again.`);
    } finally {
      setBusy('');
    }
  }

  async function togglePush() {
    setError('');
    setNotifyBusy('push');
    try {
      if (push) {
        await unsubscribeFromPush();
        await call({ method: 'POST', body: JSON.stringify({ action: 'push-unsubscribe' }) });
        setPush(false);
      } else {
        const sub = await subscribeToPush();
        if (!sub) {
          setError('Notifications were blocked. Allow them in your browser settings.');
          return;
        }
        await call({
          method: 'POST',
          body: JSON.stringify({ action: 'push-subscribe', subscription: sub }),
        });
        setPush(true);
      }
    } catch {
      setError("Couldn't change your push notifications.");
    } finally {
      setNotifyBusy('');
    }
  }

  if (error && pending === null) {
    return (
      <div className="card">
        <h2>🛡️ Approvals</h2>
        <p className="error">{error}</p>
      </div>
    );
  }

  if (pending === null) {
    return (
      <div className="card">
        <h2>🛡️ Approvals</h2>
        <p>Loading…</p>
      </div>
    );
  }

  return (
    <>
      {justApproved && (
        <div className="card text-reminder">
          <h2>📲 Text {justApproved.name || justApproved.email}</h2>
          <p>
            <strong>{justApproved.name}</strong> is approved, but nothing tells them
            automatically — the site promises an admin will text them.
          </p>
          <p className="attendee-meta">
            {formatPhone(justApproved.phone)} — the message is written for you, just hit send.
          </p>
          <div className="form-actions">
            <a
              href={approvalSmsHref(justApproved.phone, justApproved.name, firstNameOf(adminName))}
              className="sms-action"
            >
              <button type="button">📲 Text {firstNameOf(justApproved.name, 'them')}</button>
            </a>
            <button type="button" className="btn-secondary" onClick={() => setJustApproved(null)}>
              Done
            </button>
          </div>
        </div>
      )}

      <div className="card">
        <h2>🔔 Notify Me</h2>
        <p>Get told when someone registers, so approvals don't sit waiting.</p>

        <div className="notify-row">
          <div>
            <strong>Email</strong>
            <div className="attendee-meta">
              On — every organizer is emailed from southeastcogcamping.com when
              someone registers.
            </div>
          </div>
        </div>

        <div className="notify-row">
          <div>
            <strong>Push notification</strong>
            <div className="attendee-meta">
              {support === 'ios-needs-home-screen'
                ? 'On iPhone, add this site to your Home Screen first — Safari only allows push for installed sites.'
                : support === 'unsupported'
                ? 'This browser does not support push notifications.'
                : support === 'not-configured'
                ? 'Push is not configured yet.'
                : push
                ? 'On — this device buzzes when someone registers.'
                : 'Off'}
            </div>
          </div>
          <button
            type="button"
            className={push ? 'btn-secondary' : ''}
            disabled={support !== 'ok' || notifyBusy === 'push'}
            onClick={togglePush}
          >
            {notifyBusy === 'push' ? 'Saving…' : push ? 'Turn off' : 'Turn on'}
          </button>
        </div>
      </div>

      <div className="card">
        <h2>🛡️ Approvals</h2>

        {error && <p className="error">{error}</p>}

        {pending.length === 0 ? (
          <p>No one is waiting for approval.</p>
        ) : (
          <>
            <p>
              {pending.length} {pending.length === 1 ? 'person is' : 'people are'} waiting.
              After approving, text them — they aren't notified automatically.
            </p>

            <ul className="attendee-list">
              {pending.map(p => (
                <li key={p.username}>
                  <strong>{p.name || '(no name given)'}</strong>
                  {Number(p.kids) > 0 && (
                    <span className="attendee-meta">
                      {' '}
                      +{p.kids} {Number(p.kids) === 1 ? 'child' : 'children'}
                    </span>
                  )}

                  <div className="attendee-meta">{p.email}</div>
                  <div className="attendee-meta">
                    <a href={`sms:${p.phone}`}>{formatPhone(p.phone)}</a>
                  </div>

                  {(p.needsTent || p.needsRide || p.offersTent || p.offersRide) && (
                    <div className="needs">
                      {p.needsRide && <span className="need-badge">🚗 Needs a ride</span>}
                      {p.needsTent && <span className="need-badge">⛺ Needs a tent</span>}
                      {p.offersRide && <span className="offer-badge">🚗 Can give a ride</span>}
                      {p.offersTent && <span className="offer-badge">⛺ Can share a tent</span>}
                    </div>
                  )}

                  {p.dietary && (
                    <div className="allergy-banner">
                      ⚠️ Food allergy / dietary: {p.dietary}
                    </div>
                  )}

                  {p.bringing && p.bringing !== '-' && (
                    <div className="attendee-meta">Bringing: {p.bringing}</div>
                  )}

                  {confirmingDecline === p.username ? (
                    <>
                      <p className="error">
                        Decline and permanently delete this request? They would have to
                        register again.
                      </p>
                      <div className="form-actions">
                        <button
                          type="button"
                          className="btn-danger"
                          disabled={busy === p.username}
                          onClick={() => handleDecline(p)}
                        >
                          {busy === p.username ? 'Declining…' : 'Yes, delete it'}
                        </button>
                        <button
                          type="button"
                          className="btn-secondary"
                          disabled={busy === p.username}
                          onClick={() => setConfirmingDecline('')}
                        >
                          Cancel
                        </button>
                      </div>
                    </>
                  ) : (
                    <div className="form-actions">
                      <button
                        type="button"
                        disabled={busy === p.username}
                        onClick={() => handleApprove(p)}
                      >
                        {busy === p.username ? 'Approving…' : 'Approve'}
                      </button>
                      <button
                        type="button"
                        className="btn-danger"
                        disabled={busy === p.username}
                        onClick={() => setConfirmingDecline(p.username)}
                      >
                        Decline
                      </button>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </>
  );
}
